import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useSimpleMode } from "@/contexts/SimpleModeContext";
import { SimpleMetric } from "@/components/cbs/SimpleMetric";
import {
  Plus,
  Minus,
  Camera,
  Trash2,
  Thermometer,
  Droplets,
  UtensilsCrossed,
  Skull,
  Scale,
} from "lucide-react";

interface DailyRecordFormProps {
  maxBirds: number;
  onSubmit: (data: DailyRecordData) => void;
  isSubmitting: boolean;
  batchId: string;
}

export interface DailyRecordData {
  feedKg: number;
  mortalityCount: number;
  waterLiters: number;
  tempMin: number | null;
  tempMax: number | null;
  weightSamples: number[];
  notes: string;
}

function NumericStepper({
  value,
  onChange,
  min,
  max,
  step = 1,
  label,
  icon: Icon,
  unit,
  decimals = 0,
  emoji,
  simpleMode,
}: {
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
  label: string;
  icon: React.ElementType;
  unit?: string;
  decimals?: number;
  emoji?: string;
  simpleMode?: boolean;
}) {
  const clamp = (v: number) => Math.min(max, Math.max(min, v));

  return (
    <div className="space-y-1.5">
      <Label className={cn(
        "flex items-center gap-1.5 font-medium text-foreground",
        simpleMode ? "text-base" : "text-sm"
      )}>
        {simpleMode && emoji ? (
          <span className="text-2xl">{emoji}</span>
        ) : (
          <Icon className="w-4 h-4 text-muted-foreground" />
        )}
        {label}
      </Label>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={cn("shrink-0", simpleMode ? "h-14 w-14" : "h-10 w-10")}
          onClick={() => onChange(clamp(value - step))}
          disabled={value <= min}
        >
          <Minus className={cn(simpleMode ? "w-6 h-6" : "w-4 h-4")} />
        </Button>
        <Input
          type="number"
          inputMode="decimal"
          value={value.toFixed(decimals)}
          onChange={(e) => {
            const n = parseFloat(e.target.value);
            if (!isNaN(n)) onChange(clamp(n));
          }}
          className={cn(
            "text-center font-semibold",
            simpleMode ? "text-2xl h-14" : "text-lg"
          )}
          min={min}
          max={max}
          step={step}
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={cn("shrink-0", simpleMode ? "h-14 w-14" : "h-10 w-10")}
          onClick={() => onChange(clamp(value + step))}
          disabled={value >= max}
        >
          <Plus className={cn(simpleMode ? "w-6 h-6" : "w-4 h-4")} />
        </Button>
        {unit && <span className={cn("text-muted-foreground shrink-0", simpleMode ? "text-lg font-semibold w-10" : "text-sm w-8")}>{unit}</span>}
      </div>
    </div>
  );
}

export const DailyRecordForm: React.FC<DailyRecordFormProps> = ({
  maxBirds,
  onSubmit,
  isSubmitting,
  batchId,
}) => {
  const { t } = useTranslation();
  const { isSimpleMode } = useSimpleMode();
  const [searchParams] = useSearchParams();
  const [feedKg, setFeedKg] = useState(0);
  const [mortalityCount, setMortalityCount] = useState(0);
  const [waterLiters, setWaterLiters] = useState(0);
  const [tempMin, setTempMin] = useState<string>("");
  const [tempMax, setTempMax] = useState<string>("");
  const [weightSamples, setWeightSamples] = useState<number[]>([]);
  const [newWeight, setNewWeight] = useState("");
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Photo state — one per metric
  const [photos, setPhotos] = useState<Record<string, boolean>>({
    feed: false,
    mortality: false,
    water: false,
    temperature: false,
    weight: false,
  });

  // Handle camera return — CBS-TODO: CameraScreen should redirect back with ?photoTaken=<metric>
  useEffect(() => {
    const photoMetric = searchParams.get('photoTaken');
    if (photoMetric && photos.hasOwnProperty(photoMetric)) {
      setPhotos(prev => ({ ...prev, [photoMetric]: true }));
    }
  }, [searchParams]);

  const handleTakePhoto = (metric: string) => {
    if ('vibrate' in navigator) navigator.vibrate(15);
    const url = `/camera?category=${metric}&batchId=${batchId}&returnTo=/daily-record`;
    window.location.href = url;
  };

  const PhotoButton = ({ metric, label }: { metric: string; label: string }) => (
    <button
      type="button"
      onClick={() => handleTakePhoto(metric)}
      className={cn(
        "w-full flex items-center justify-center gap-2 py-3 rounded-xl transition-all active:scale-95 mt-2",
        photos[metric]
          ? "bg-success/10 border-2 border-success text-success"
          : "bg-muted/50 border-2 border-dashed border-muted-foreground/30 text-muted-foreground"
      )}
    >
      {isSimpleMode ? (
        <span className="text-3xl">{photos[metric] ? '✅' : '📸'}</span>
      ) : (
        <>
          <Camera className="w-5 h-5" />
          <span className="text-sm font-medium">
            {photos[metric] ? `✅ ${label} photo taken` : `📸 Take ${label} photo`}
          </span>
        </>
      )}
    </button>
  );

  const validate = (): boolean => {
    const errs: Record<string, string> = {};

    // Allow submit as long as at least one metric has a value
    const hasAnyValue = feedKg > 0 || mortalityCount > 0 || waterLiters > 0 ||
      tempMin !== '' || tempMax !== '' || weightSamples.length > 0;
    if (!hasAnyValue) {
      errs.general = t("cbs.dailyRecord.enterAtLeastOne", {
        defaultValue: "Enter at least one metric",
      });
    }

    if (feedKg < 0 || feedKg > 5000) {
      errs.feed = t("cbs.dailyRecord.feedRange", { defaultValue: "Feed must be 0–5,000 kg" });
    }
    if (mortalityCount < 0 || mortalityCount > maxBirds) {
      errs.mortality = t("cbs.dailyRecord.mortalityRange", {
        defaultValue: `Mortality must be 0–${maxBirds}`,
      });
    }

    const tMin = tempMin ? parseFloat(tempMin) : null;
    const tMax = tempMax ? parseFloat(tempMax) : null;
    if (tMin !== null && (tMin < 15 || tMin > 45)) {
      errs.tempMin = "15–45°C";
    }
    if (tMax !== null && (tMax < 15 || tMax > 45)) {
      errs.tempMax = "15–45°C";
    }
    if (tMin !== null && tMax !== null && tMin > tMax) {
      errs.tempMin = t("cbs.dailyRecord.tempMinMax", { defaultValue: "Min must be ≤ Max" });
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    onSubmit({
      feedKg,
      mortalityCount,
      waterLiters,
      tempMin: tempMin ? parseFloat(tempMin) : null,
      tempMax: tempMax ? parseFloat(tempMax) : null,
      weightSamples,
      notes,
    });
  };

  const addWeight = () => {
    const w = parseFloat(newWeight);
    if (!isNaN(w) && w > 0 && w < 10000) {
      setWeightSamples((prev) => [...prev, w]);
      setNewWeight("");
    }
  };

  const removeWeight = (idx: number) => {
    setWeightSamples((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {errors.general && (
        <div className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2">
          {errors.general}
        </div>
      )}

      {/* Feed */}
      <Card className="p-4">
        <NumericStepper
          value={feedKg}
          onChange={setFeedKg}
          min={0}
          max={5000}
          step={0.5}
          decimals={1}
          label={t("cbs.dailyRecord.feed", { defaultValue: "Feed (kg)" })}
          icon={UtensilsCrossed}
          unit="kg"
          emoji={"\uD83C\uDF3E"}
          simpleMode={isSimpleMode}
        />
        {errors.feed && <p className="text-xs text-destructive mt-1">{errors.feed}</p>}
      </Card>
      <PhotoButton metric="feed" label="feed" />

      {/* Mortality */}
      <Card className="p-4">
        <NumericStepper
          value={mortalityCount}
          onChange={setMortalityCount}
          min={0}
          max={maxBirds}
          step={1}
          label={t("cbs.dailyRecord.mortality", { defaultValue: "Mortality (birds)" })}
          icon={Skull}
          emoji={"\uD83D\uDC80"}
          simpleMode={isSimpleMode}
        />
        {errors.mortality && <p className="text-xs text-destructive mt-1">{errors.mortality}</p>}
      </Card>
      <PhotoButton metric="mortality" label="mortality" />

      {/* Water */}
      <Card className="p-4">
        <NumericStepper
          value={waterLiters}
          onChange={setWaterLiters}
          min={0}
          max={50000}
          step={1}
          label={t("cbs.dailyRecord.water", { defaultValue: "Water (litres)" })}
          icon={Droplets}
          unit="L"
          emoji={"\uD83D\uDCA7"}
          simpleMode={isSimpleMode}
        />
      </Card>
      <PhotoButton metric="water" label="water" />

      {/* Temperature */}
      <Card className="p-4 space-y-3">
        <Label className={cn(
          "flex items-center gap-1.5 font-medium text-foreground",
          isSimpleMode ? "text-base" : "text-sm"
        )}>
          {isSimpleMode ? (
            <span className="text-2xl">{"\uD83C\uDF21\uFE0F"}</span>
          ) : (
            <Thermometer className="w-4 h-4 text-muted-foreground" />
          )}
          {t("cbs.dailyRecord.temperature", { defaultValue: "Temperature" })}
        </Label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-muted-foreground">{"Min \u00B0C"}</Label>
            <Input
              type="number"
              inputMode="decimal"
              placeholder="e.g. 25"
              value={tempMin}
              onChange={(e) => setTempMin(e.target.value)}
              min={15}
              max={45}
              step={0.1}
              className={cn(isSimpleMode && "h-14 text-xl text-center")}
            />
            {errors.tempMin && <p className="text-xs text-destructive mt-0.5">{errors.tempMin}</p>}
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">{"Max \u00B0C"}</Label>
            <Input
              type="number"
              inputMode="decimal"
              placeholder="e.g. 34"
              value={tempMax}
              onChange={(e) => setTempMax(e.target.value)}
              min={15}
              max={45}
              step={0.1}
              className={cn(isSimpleMode && "h-14 text-xl text-center")}
            />
            {errors.tempMax && <p className="text-xs text-destructive mt-0.5">{errors.tempMax}</p>}
          </div>
        </div>
      </Card>
      <PhotoButton metric="temperature" label="temperature" />

      {/* Weight Samples */}
      <Card className="p-4 space-y-3">
        <Label className={cn(
          "flex items-center gap-1.5 font-medium text-foreground",
          isSimpleMode ? "text-base" : "text-sm"
        )}>
          {isSimpleMode ? (
            <span className="text-2xl">{"\u2696\uFE0F"}</span>
          ) : (
            <Scale className="w-4 h-4 text-muted-foreground" />
          )}
          {t("cbs.dailyRecord.weightSamples", { defaultValue: "Weight (g)" })}
        </Label>

        {weightSamples.length > 0 && (
          <>
            <div className="flex flex-wrap gap-2">
              {weightSamples.map((w, i) => (
                <span
                  key={i}
                  className={cn(
                    "inline-flex items-center gap-1 bg-muted rounded-full px-3 py-1",
                    isSimpleMode ? "text-lg min-h-12" : "text-sm"
                  )}
                >
                  {w}g
                  <button type="button" onClick={() => removeWeight(i)} className="text-muted-foreground hover:text-destructive min-w-[24px] min-h-[24px] flex items-center justify-center">
                    <Trash2 className={cn(isSimpleMode ? "w-5 h-5" : "w-3 h-3")} />
                  </button>
                </span>
              ))}
            </div>
            {isSimpleMode && (
              <SimpleMetric
                emoji={"\u2696\uFE0F"}
                value={Math.round(weightSamples.reduce((a, b) => a + b, 0) / weightSamples.length)}
                unit="g avg"
                size="sm"
                color="info"
              />
            )}
          </>
        )}

        <div className="flex gap-2">
          <Input
            type="number"
            inputMode="decimal"
            placeholder="e.g. 1850"
            value={newWeight}
            onChange={(e) => setNewWeight(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addWeight())}
            className={cn(isSimpleMode && "h-14 text-xl")}
          />
          <Button type="button" variant="outline" size={isSimpleMode ? "lg" : "sm"} onClick={addWeight} disabled={!newWeight} className={cn(isSimpleMode && "min-h-14 min-w-14")}>
            <Plus className="w-4 h-4 mr-1" /> {!isSimpleMode && "Add"}
          </Button>
        </div>
        {!isSimpleMode && weightSamples.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Avg: {Math.round(weightSamples.reduce((a, b) => a + b, 0) / weightSamples.length)}g
            ({weightSamples.length} samples)
          </p>
        )}
      </Card>
      <PhotoButton metric="weight" label="weight" />

      {/* Notes - hidden in simple mode */}
      {!isSimpleMode && (
        <Card className="p-4">
          <Label className="text-sm font-medium text-foreground mb-1.5 block">
            {t("cbs.dailyRecord.notes", { defaultValue: "Notes (optional)" })}
          </Label>
          <Textarea
            placeholder={t("cbs.dailyRecord.notesPlaceholder", { defaultValue: "Any observations\u2026" })}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
          />
        </Card>
      )}

      {/* Submit */}
      <Button type="submit" className={cn("w-full", isSimpleMode ? "h-16 text-xl" : "")} size="lg" disabled={isSubmitting}>
        {isSubmitting
          ? t("common.saving", { defaultValue: "Saving\u2026" })
          : isSimpleMode ? "\u2705 Save" : t("cbs.dailyRecord.submit", { defaultValue: "Save Record" })}
      </Button>
    </form>
  );
};
